import React from 'react';

import { AppSidebar } from '../app-sidebar.js';
import { SidebarProvider } from '../ui/sidebar.js';

import { OfficeUpdateBanner } from './OfficeUpdateBanner.js';
import { ThemedHeader } from './ThemedHeader.js';

export interface ThemedLayoutProps {
  children: React.ReactNode;
  className?: string;
  containerWidth?: 'full' | 'narrow' | 'medium' | 'wide';
  padding?: 'none' | 'small' | 'medium' | 'large';
  user?: any; // Accept user prop for SSR hydration
}

/**
 * Themed layout component that applies theme colors to the page
 * This component should wrap the main content of each page
 */
export const ThemedLayout: React.FC<ThemedLayoutProps> = ({
  children,
  className = '',
  containerWidth = 'medium',
  padding = 'medium',
  user, // Accept user prop
}) => {
  // Removed useThemeStyles hook to avoid hook context issues
  // Theme classes are now handled by CSS and GlobalThemeApplicator

  // Determine container width class
  const containerClass = containerWidth === 'full'
    ? 'w-full'
    : containerWidth === 'narrow'
      ? 'max-w-3xl mx-auto'
      : containerWidth === 'wide'
        ? 'max-w-7xl mx-auto'
        : 'max-w-5xl mx-auto';

  // Determine padding class
  const paddingClass = padding === 'none'
    ? ''
    : padding === 'small'
      ? 'px-4 py-4'
      : padding === 'large'
        ? 'px-8 py-8'
        : 'px-6 py-6';

  // Combine classes for the main layout div (no padding here)
  const layoutClasses = `
    flex 
    flex-col 
    min-h-screen 
    bg-background
    text-foreground
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // Combine classes for the main content area
  const mainClasses = `
    ${paddingClass}
    h-[calc(100vh-4rem)]
  `.trim().replace(/\s+/g, ' ');
  // Removed: h-[91vh], absolute, mt-[4rem], w-[86vw]

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full overflow-hidden">
        <AppSidebar />
        <div className={`${layoutClasses} flex-1 min-w-0 overflow-hidden`}>
          <header className="flex h-16 shrink-0 items-center gap-2 shadow-sm border-b">
            <div className="flex items-center gap-2 px-4 flex-1 min-w-0">
              <ThemedHeader user={user} />
            </div>
          </header>
          <main className={`${mainClasses} flex-1 overflow-auto`}>
            {children}
          </main>
        </div>
      </div>
      <OfficeUpdateBanner />
    </SidebarProvider>
  );
};

export default ThemedLayout;
